/**
 * Transform functions for EAP benefits
 *
 * These functions handle the bi-directional transformation between:
 * - Frontend grid format (flat fields)
 * - Backend API format (same structure for EAP since it's already flat)
 *
 * Note: For EAP, the grid and API formats are identical since all fields
 * are simple values without nested structures. These transform functions
 * exist for consistency with other LOCs and to allow for future changes.
 */

import {
  shouldIncludeBenefitInApi,
  cleanupNotDisclosedBenefits,
} from "../shared/builders";

import type { EapBenefitsApi, EapBenefitsGrid } from "./types";

/**
 * Transforms EAP benefits from frontend grid format to API format
 *
 * For EAP, this is essentially a pass-through since the structures are identical,
 * but ensures we only include defined fields.
 *
 * @param benefits - The benefits object from the frontend grid
 * @returns Transformed benefits object matching the API structure
 */
export const transformEapBenefitsToApi = (
  benefits: EapBenefitsGrid,
): EapBenefitsApi => {
  const transformed: EapBenefitsApi = {
    objectType: "eap",
  };

  // Simple field mappings - include only if defined
  if (benefits.participationRequirements !== undefined) {
    transformed.participationRequirements = benefits.participationRequirements;
  }

  if (benefits.faceToFaceVisits !== undefined) {
    transformed.faceToFaceVisits = benefits.faceToFaceVisits;
  }

  if (benefits.perOccurrencePerYear !== undefined) {
    transformed.perOccurrencePerYear = benefits.perOccurrencePerYear;
  }

  if (shouldIncludeBenefitInApi(benefits.unlimitedTelephonic)) {
    transformed.unlimitedTelephonic = benefits.unlimitedTelephonic;
  }

  if (benefits.legalFinancialResources !== undefined) {
    transformed.legalFinancialResources = benefits.legalFinancialResources;
  }

  if (shouldIncludeBenefitInApi(benefits.tiedToAnotherLoc)) {
    transformed.tiedToAnotherLoc = benefits.tiedToAnotherLoc;
  }

  if (shouldIncludeBenefitInApi(benefits.employerContribution)) {
    transformed.employerContribution = benefits.employerContribution;
  }

  if (benefits.benefitClassAvailability !== undefined) {
    transformed.benefitClassAvailability = benefits.benefitClassAvailability;
  }

  // Extended (non-v5) fields. Sent as a full object (a full-replace PATCH,
  // like the rest of `benefits`) whenever at least one extended field is set,
  // so an edit to any other field on the plan doesn't drop the extended values
  // already on it. Omitted entirely when every extended field is unset, so an
  // edit that never touches these fields doesn't create an empty extended row
  // on the backend.
  const extended = {
    additionalResourcesIncluded: benefits.additionalResourcesIncluded,
  };
  if (
    Object.values(extended).some((value) => value !== undefined && value !== "")
  ) {
    transformed.extended = extended;
  }

  return cleanupNotDisclosedBenefits(transformed) as EapBenefitsApi;
};

/**
 * Transforms EAP benefits from API format to frontend grid format
 *
 * For EAP, this is essentially a pass-through since the structures are identical.
 *
 * @param benefits - The benefits object from the API
 * @returns Transformed benefits object matching the grid structure
 */
export const transformEapBenefitsFromApi = (
  benefits: EapBenefitsApi,
): EapBenefitsGrid => {
  const transformed: EapBenefitsGrid = {
    objectType: "eap",
  };

  // Simple field mappings - copy all defined fields
  if (benefits.participationRequirements !== undefined) {
    transformed.participationRequirements = benefits.participationRequirements;
  }

  if (benefits.faceToFaceVisits !== undefined) {
    transformed.faceToFaceVisits = benefits.faceToFaceVisits;
  }

  if (benefits.perOccurrencePerYear !== undefined) {
    transformed.perOccurrencePerYear = benefits.perOccurrencePerYear;
  }

  if (benefits.unlimitedTelephonic !== undefined) {
    transformed.unlimitedTelephonic = benefits.unlimitedTelephonic;
  }

  if (benefits.legalFinancialResources !== undefined) {
    transformed.legalFinancialResources = benefits.legalFinancialResources;
  }

  if (benefits.tiedToAnotherLoc !== undefined) {
    transformed.tiedToAnotherLoc = benefits.tiedToAnotherLoc;
  }

  if (benefits.employerContribution !== undefined) {
    transformed.employerContribution = benefits.employerContribution;
  }

  if (benefits.benefitClassAvailability !== undefined) {
    transformed.benefitClassAvailability = benefits.benefitClassAvailability;
  }

  // Extended (non-v5) fields. Every grid key is present (as undefined) even
  // when `extended` itself is absent, so a later edit to another field doesn't
  // leave a stale extended value in the grid row from a previous plan.
  transformed.additionalResourcesIncluded =
    benefits.extended?.additionalResourcesIncluded ?? undefined;

  return transformed;
};

/** Grid keys that persist under `benefits.extended` — see STD_EXTENDED_GRID_FIELD_KEYS. */
export const EAP_EXTENDED_GRID_FIELD_KEYS: ReadonlySet<string> = new Set([
  "additionalResourcesIncluded",
]);
